import { Handle, Position, type NodeProps } from 'reactflow';
import { TOPOLOGY_COLOR_CLASSES } from './color';
import type { BranchNodeData, ClientNodeData, LeafNodeData } from './buildGraph';

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

/**
 * Cajas del diagrama.
 *
 * Fase 3: cliente y sucursal ya se colorean con la agregación de `buildGraph`
 * (sección 3: el peor color de los hijos manda), igual que el nodo hoja desde
 * la fase 2. Ninguna de las tres recalcula el color aquí — lo consumen ya
 * resuelto en `data.color`. Los `Handle` siguen invisibles: son el punto de
 * anclaje que React Flow necesita para trazar la arista, no un control que el
 * usuario deba ver.
 */

const boxClass =
  'rounded-lg border border-black/15 dark:border-white/20 bg-[var(--occ-surface)] px-3 py-2 text-sm shadow-sm';

export function ClientNodeView({ data }: NodeProps<ClientNodeData>) {
  return (
    <div className={cx(boxClass, TOPOLOGY_COLOR_CLASSES[data.color], 'cursor-pointer select-none')}>
      <Handle type="target" position={Position.Top} className="opacity-0" />
      <div className="font-semibold tracking-tight">{data.label}</div>
      {data.collapsed && (
        <div className="text-xs opacity-70">
          {data.onlineCount}/{data.totalCount} online
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="opacity-0" />
    </div>
  );
}

export function BranchNodeView({ data }: NodeProps<BranchNodeData>) {
  return (
    <div className={cx(boxClass, TOPOLOGY_COLOR_CLASSES[data.color], 'cursor-pointer select-none')}>
      <Handle type="target" position={Position.Top} className="opacity-0" />
      <div className="opacity-85">{data.label}</div>
      {data.collapsed && (
        <div className="text-xs opacity-70">
          {data.onlineCount}/{data.totalCount} online
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="opacity-0" />
    </div>
  );
}

export function LeafNodeView({ data }: NodeProps<LeafNodeData>) {
  return (
    <div className={cx(boxClass, 'cursor-pointer', TOPOLOGY_COLOR_CLASSES[data.color])}>
      <Handle type="target" position={Position.Top} className="opacity-0" />
      <div className="opacity-90">{data.alias}</div>
    </div>
  );
}

export const topologyNodeTypes = {
  client: ClientNodeView,
  branch: BranchNodeView,
  leaf: LeafNodeView,
};
